import { useRef, useState } from 'react';
import {
  Grid,
  GridCellProps,
  GridColumn as Column,
  GridDataStateChangeEvent,
  GridNoRecords
} from '@progress/kendo-react-grid';
import { State, toDataSourceRequestString } from '@progress/kendo-data-query';
import { ColumnMenu } from '../components/columnMenu';
import { ExcelExport } from '@progress/kendo-react-excel-export';
import { DefaultGridSettings } from '../constants/grid-settings';
import {
  GridToolBar,
  GridToolbarDataStateChangeEvent
} from '../components/grid/grid-tool-bar';
import Toolbar from '../components/page-toolbar';
import { NoRecords } from '../components/grid/no-records';
import { formatDateToDDMMYYYY } from '../utils/date';
import { ActionButtonsCell } from '../components/grid/action-buttons-cell';
import { ZonePendingPreviewWindow } from './zone-library-preview/pending-window';
import { usePendingZones } from '../hooks/services/use-zones-library';
import { Loader } from '@progress/kendo-react-indicators';

const initialDataState: State = {
  sort: [{ field: 'name', dir: 'asc' }],
  ...DefaultGridSettings.initialDataState
};

export default function ZonePendingGrid() {
  const [dataState, setDataState] = useState<State>(initialDataState);

  const [showPreviewModal, setShowPreviewModal] = useState<string>('');

  const { data, isLoading, mutate } = usePendingZones(
    toDataSourceRequestString(dataState)
  );

  const gridExportRef = useRef<ExcelExport | null>(null);

  const DateCell = (props: GridCellProps) => {
    return (
      <td className={'k-table-td'}>
        {formatDateToDDMMYYYY(props.dataItem.createdDateUtc, true)}
      </td>
    );
  };

  const SourceCell = (props: GridCellProps) => {
    return (
      <td className={'k-table-td'}>{props.dataItem.source || 'Imported'}</td>
    );
  };

  const ActionButtonsCellWrapper = (props: GridCellProps) => (
    <ActionButtonsCell
      {...props}
      actions={[
        {
          onClick: () => setShowPreviewModal(props.dataItem.id.toString()),
          icon: 'visibility',
          themeColor: 'primary',
          title: 'View'
        }
      ]}
    />
  );

  return (
    <>
      <Toolbar title={'Zone Maps Pending Approval'}></Toolbar>
      <div
        className={'pt-2'}
        style={{
          flexGrow: 1,
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0,
          width: '100%',
          overflow: 'hidden'
        }}>
        <GridToolBar
          searchPlaceholder={'Search zones'}
          columnsToSearch={['name', 'createdBy', 'source']}
          exportRef={gridExportRef}
          showCardMode={false}
          {...dataState}
          onDataStateChange={(e: GridToolbarDataStateChangeEvent) => {
            const newState = {
              ...e.dataState,
              filter: {
                logic: 'and',
                filters: e.dataState.filter ? [e.dataState.filter] : []
              }
            } as State;
            setDataState(newState);
          }}></GridToolBar>
        <ExcelExport
          data={data?.data}
          ref={gridExportRef}
          fileName={`zones-pending.xlsx`}>
          <Grid
            id={'grid-zone-pending'}
            style={{
              flexGrow: 1,
              minHeight: 0,
              height: '100%',
              overflow: 'hidden'
            }}
            pageable={DefaultGridSettings.pagerSettings}
            sortable={true}
            data={data}
            {...dataState}
            onDataStateChange={(e: GridDataStateChangeEvent) => {
              setDataState(e.dataState);
            }}>
            <Column field={'name'} title={'Name'} columnMenu={ColumnMenu} />
            <Column
              field={'source'}
              title={'Game'}
              cells={{ data: SourceCell }}
              columnMenu={ColumnMenu}
            />
            <Column
              field={'createdBy'}
              title={'Game Designer'}
              columnMenu={ColumnMenu}
            />
            <Column
              field={'createdDateUtc'}
              title={'Date Created'}
              cells={{ data: DateCell }}
            />
            <Column cells={{ data: ActionButtonsCellWrapper }} width={120} />

            <GridNoRecords>
              {isLoading ? (
                <div
                  className={
                    'd-flex flex-column align-items-center gap-2 text-muted'
                  }>
                  <Loader type={'pulsing'} />
                  <span>Loading data</span>
                </div>
              ) : (
                <NoRecords />
              )}
            </GridNoRecords>
          </Grid>
        </ExcelExport>
      </div>

      {showPreviewModal && (
        <ZonePendingPreviewWindow
          zoneId={showPreviewModal}
          onClose={() => {
            setShowPreviewModal('');
            mutate();
          }}
        />
      )}
    </>
  );
}
